"use client";

import { createEffect, createEvent, createStore, sample } from "effector";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { AnalyticsService } from "@features/shared/analytics";
import { IAppRouteUnitsContract } from "@features/app/route-units";

export type Platform = "web" | "android" | "ios";

export interface LaunchContext {
    platform: Platform;
    isApp: boolean;
}

export const setLaunchContext = createEvent<Platform>();
export const setUA = createEvent<UAParser.IResult>();

const getDefaultContext = () => {
    const platform = ((typeof localStorage !== "undefined" && localStorage.getItem("tixu_app_platform")) ||
        "web") as Platform;
    AnalyticsService.setPlatform(platform);
    return { isApp: ["android", "ios"].includes(platform), platform };
};

export const $launchContext = createStore<LaunchContext>(getDefaultContext());
const $ua = createStore<UAParser.IResult | null>(null, { skipVoid: false }).on(setUA, (_, ua) => ua);
const $browser = $ua.map((ua) => ua?.browser.name, { skipVoid: false });
const $os = $ua.map((ua) => ua?.os.name, { skipVoid: false });
const $isMetaBrowser = $ua.map(
    (ua) => {
        const agent = ua?.ua ?? "";
        return /Instagram/i.test(agent) || /FBAN|FBAV/i.test(agent)
    },
    { skipVoid: false }
);

export const $isApp = $launchContext.map(({ isApp }) => isApp);
export const $platform = $launchContext.map(({ platform }) => platform);

const setAnalyticsPlatform = createEffect((platform: Platform) => AnalyticsService.setPlatform(platform));
const setTixuAppPlatform = createEffect((platform: Platform) => localStorage.setItem("tixu_app_platform", platform));

sample({
    clock: setLaunchContext,
    fn: (platform: Platform) => ({ isApp: ["android", "ios"].includes(platform), platform }),
    target: $launchContext
});

sample({
    clock: setLaunchContext,
    target: setTixuAppPlatform
});

sample({
    clock: setLaunchContext,
    target: setAnalyticsPlatform
});

const contract = new ModelContract(
    {
        $launchContext,
        $isApp,
        $platform,
        $browser,
        $os,
        $isMetaBrowser
    },
    {
        setLaunchContext
    },
    {},
    {}
);

export type IPlatformContextContract = typeof contract;

export class PlatformContextModule extends ModuleDescription<IPlatformContextContract> {
    constructor(public routesModule: IAppRouteUnitsContract) {
        super();
    }
    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: this.routesModule.stores.iosApp.$isActive,
            source: {
                query: this.routesModule.stores.iosApp.$query
            },
            filter: (s) => s.query.platform === "ios",
            fn: () => "ios" as Platform,
            target: setLaunchContext
        });
    }
}
